// src/components/desk/contract/paper.js — the designer's tokens for the contract surfaces
// (design/CONTRACTS_DESIGNER_HANDOFF_2026-09-11.md, round 1) as Tailwind class strings, so
// every contract piece reads the same three colours from ONE place.
//
//   paper  #F7F5F0   the sheet the contract is written on
//   ink    #14130F   the text, and the dark bars around the paper (header, sticky footer,
//                    the cards on the right column, the meeting-card block)
//   accent #1F6F4F   the one colour: the "you fill" tag, the {{…}} value chips, CLOSED
//
// The Desk keeps its dark shell; INSIDE it the contract is a sheet of paper (light card, ink
// text) — this page is the one place in the Desk that is light-on-dark-shell by design. No
// global CSS: arbitrary Tailwind values only. Font: Noto Sans Georgian (already loaded by
// the app; nothing here sets a family).

import { useEffect, useState } from 'react'
import { getLanguage, tOr } from '../../../lib/i18n.js'

// Label in the UI language, falling back to the other one rather than to a blank. Defined
// HERE (not in ContractComposer.jsx, which re-exports it) so ClauseEditor / ContractSections
// can read a label without importing the composer that imports them — no module cycle.
export const ka = () => getLanguage() === 'ka'
export const L = (row, base) => (ka() ? row?.[`${base}_ka`] || row?.[`${base}_en`] : row?.[`${base}_en`] || row?.[`${base}_ka`]) || ''

// 📏 WHAT AN OPTION IS CALLED ON THE CONTROL vs WHAT IT SAYS IN THE CONTRACT (owner 2026-09-12:
// "long texts still overflow"). An option's `label_*` is PRINTED — clause 6 substitutes it into
// "ქვექირავნობა და ქირავნობის უფლების გადაცემა: {{sublet}}" — so two of them are whole sentences
// and a native <select>, which cannot wrap, clipped them mid-phrase: both sublet options begin
// "დაიშვება…", so the agent could not tell them apart. Shortening the label was the obvious move
// and would have degraded the agreement itself.
// So 1096 added an OPTIONAL `short_*` beside it: the picker asks for the short form and falls
// back to the label, which is why an option without one renders exactly as it always did.
// ⚠️ Only the FORM may use this. The renderer must keep printing `label_*`, or the wording of a
// signed contract starts depending on how wide somebody's phone was.
export const optionText = o => L(o, 'short') || L(o, 'label') || o?.value || ''

export const PAPER = '#F7F5F0'
export const INK = '#14130F'
export const ACCENT = '#1F6F4F'

// The sheet itself, and text on it.
// ⚠️ [color-scheme:light] is load-bearing: the Desk is `.dark` (index.css), so on this LIGHT sheet the browser drew every
//    unticked checkbox and radio as a solid dark square — an empty box read as a ticked one (owner 2026-10-12).
export const SHEET = 'bg-[#F7F5F0] text-[#14130F] [color-scheme:light] rounded-2xl shadow-[0_1px_0_rgba(20,19,15,.06),0_8px_24px_-12px_rgba(20,19,15,.35)]'
// A dark bar or card around the sheet.
export const BAR = 'bg-[#14130F] text-[#F7F5F0]'
// Muted ink for labels and meta on the paper.
export const INK_MUTED = 'text-[#14130F]/55'
export const INK_FAINT = 'text-[#14130F]/35'
// A group heading on the paper: "PARTIES", "TERM & MONEY" — small mono caps.
export const GROUP = 'font-mono text-[10px] tracking-[.14em] uppercase text-[#14130F]/50'
// The small mono source tag at the right of a value row.
export const TAG = 'font-mono text-[10px] tracking-[.02em] whitespace-nowrap text-[#14130F]/45'
export const TAG_ASK = 'font-mono text-[10px] tracking-[.02em] whitespace-nowrap text-[#1F6F4F] font-medium'
// 🍎 A FORM CONTROL UNDER 16px MAKES iOS SAFARI ZOOM THE WHOLE PAGE — AND IT NEVER ZOOMS BACK.
// Owner, phone testing 2026-09-12, on the ქვექირავნობა select: "if selected also breaks mobile
// view". It is not an overflow — the earlier width work (max-w-full / min-w-0 / flex-wrap) holds,
// and measuring the select showed it never exceeding its row. It is Safari's focus behaviour:
// tapping any input/select/textarea whose font-size is below 16px scales the LAYOUT VIEWPORT up
// so the text is readable, and closing the picker leaves the page scaled — the header is clipped,
// the sheet sits off to one side, and everything looks broken until the agent pinches out by hand.
// The tell in the screenshot was the HEADER being cut and its icons drawn LARGER: an overflow
// moves the content inside a full-width header, only a zoom enlarges the header itself.
// So every control on this surface is 16px up to the sm breakpoint and keeps its designed size
// from 640px on, where no touch keyboard and no auto-zoom exist. Density on a phone costs less
// than a page that has to be pinched back into place after every answer.
// Inputs ON the paper (the Desk's `inp` is dark; a dark box on a light sheet reads as a hole).
export const PINP = 'rounded-md border border-[#14130F]/15 bg-white text-[#14130F] px-2.5 py-1.5 min-h-[34px] text-[16px] sm:text-[13px] leading-snug '
  + 'placeholder:text-[#14130F]/30 focus:outline-none focus:ring-2 focus:ring-[#1F6F4F]/40 focus:border-[#1F6F4F]/60 disabled:bg-[#14130F]/5 disabled:text-[#14130F]/60'
// The same, for the segmented date box (SegmentedInput takes a full className).
export const PDATE = 'inline-flex items-center gap-0.5 rounded-md border border-[#14130F]/15 bg-white text-[#14130F] px-2.5 py-1.5 text-[16px] sm:text-[13px] '
  + 'focus-within:outline-none focus-within:ring-2 focus-within:ring-[#1F6F4F]/40'
// The accent button (Create / Save / Done) and its quiet sibling.
export const BTN_ACCENT = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#1F6F4F] text-[#F7F5F0] px-4 py-2 text-sm font-medium '
  + 'hover:brightness-110 disabled:bg-[#F7F5F0]/15 disabled:text-[#F7F5F0]/40 disabled:cursor-not-allowed'
export const BTN_ON_BAR = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#F7F5F0]/25 text-[#F7F5F0] px-3 py-2 text-sm '
  + 'hover:bg-[#F7F5F0]/10 disabled:opacity-40 disabled:cursor-not-allowed'
export const BTN_ON_PAPER = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#14130F]/20 text-[#14130F] px-3 py-1.5 text-[13px] '
  + 'hover:bg-[#14130F]/5 disabled:opacity-40 disabled:cursor-not-allowed'
// The one red-outlined button: "Change — signatures will be discarded".
export const BTN_DANGER_OUTLINE = 'inline-flex items-center justify-center gap-1.5 rounded-lg border-2 border-red-700 text-red-700 px-4 py-2 text-sm font-medium '
  + 'hover:bg-red-700/5 disabled:opacity-40 disabled:cursor-not-allowed'
// A {{value}} marker inside a clause, rendered read-only.
export const MARKER = 'inline-block align-baseline rounded bg-[#1F6F4F]/10 text-[#1F6F4F] font-mono text-[11px] px-1 leading-5 whitespace-nowrap'

// Desktop = the designer's ≥ 1024 px (Tailwind `lg:`). A hook, because the clause editor is
// a full-screen SHEET on a phone and an inline side-by-side block on a desktop — the same
// question CSS answers for layout, answered once for the JS branch. Listens, so a window
// dragged across the threshold (the harness slider does not move the viewport, the pane's
// resize does) switches without a reload.
const WIDE_QUERY = '(min-width: 1024px)'
export function useWide() {
  const [wide, setWide] = useState(() => {
    try { return window.matchMedia(WIDE_QUERY).matches } catch { return false }
  })
  useEffect(() => {
    let mq
    try { mq = window.matchMedia(WIDE_QUERY) } catch { return undefined }
    const on = e => setWide(!!e.matches)
    setWide(!!mq.matches)
    if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on)
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on) }
  }, [])
  return wide
}

// "12:22" — the timeline's short time (the date is in the tooltip / the history list).
export function fmtClock(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }
  catch { return String(iso).slice(11, 16) }
}

// THE SHORT NAME (owner 2026-09-11: "let's show these names here instead of long names").
// A contract's `title_ka` is the LEGAL title printed on the document ("საცხოვრებელი უძრავი
// ქონების ქირავნობის ხელშეკრულება") and must stay exactly that. Every LIST — the meeting
// panel, the type chooser, the page header, the related strip — says the short name instead,
// keyed on the template KIND with the code as a fallback so a template added later still gets
// one. The full title stays as the `title` attribute for anyone who wants it.
const KIND_OF = { rent_residential_v1: 'rent_residential', rent_commercial_v1: 'rent_commercial', agency_service_v1: 'agency_service', tenant_agency_service_v1: 'tenant_agency_service', binder_v1: 'binder', owner_mandate_v1: 'owner_mandate' }
export function contractKindOf(x) {
  return x?.kind || x?.template_kind || KIND_OF[x?.template_code || x?.code] || null
}
// `x` is a template ({code,kind,title_ka…}) or a contract ({template_code,template_title_ka…}).
export function contractShortName(x) {
  const kind = contractKindOf(x)
  const short = kind ? tOr(`desk_contract_kind_${kind}`, '') : ''
  if (short && short !== `desk_contract_kind_${kind}`) return short
  return L(x, x?.template_title_ka !== undefined || x?.template_title_en !== undefined ? 'template_title' : 'title') || x?.template_code || x?.code || ''
}
export function contractFullTitle(x) {
  return L(x, x?.template_title_ka !== undefined || x?.template_title_en !== undefined ? 'template_title' : 'title') || x?.template_code || x?.code || ''
}
